import React, { FC, useEffect, useMemo, useState } from 'react';
import { useContractTemplate, useContractTemplateActions } from '../../../hooks/api/ContractTemplates/useContractTemplates';
import {
  buildFieldsPayload, clickToPdfPoint, ContractTemplateField, ContractVariable, groupVariables, isCheckboxType,
  pdfPointToCanvas,
} from '../../../utility/contractTemplates';
import { getErrorMessages } from '../../../utility/team';
import PdfPageView from './PdfPageView';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const VariableSelect: FC<{ id: string; value: string; variables: ContractVariable[]; onChange: (v: string) => void }> = ({ id, value, variables, onChange }) => (
  <select id={id} aria-label="Variable" className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">Not used</option>
    {groupVariables(variables).map((g) => (
      <optgroup key={g.group} label={g.group}>
        {g.items.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
      </optgroup>
    ))}
  </select>
);

// ---- Fillable PDFs: match each form field to a REIX variable ---------------------------------

const FillableMapper: FC<{
  fields: ContractTemplateField[];
  pdfFields: { name: string; type: string; page: number }[];
  variables: ContractVariable[];
  readOnly: boolean;
  onChange: (fields: ContractTemplateField[]) => void;
}> = ({ fields, pdfFields, variables, readOnly, onChange }) => {
  const byName = useMemo(() => new Map(fields.map((f) => [ f.pdf_field_name, f ])), [ fields ]);
  const [ pageFilter, setPageFilter ] = useState(0);
  const pages = Array.from(new Set(pdfFields.map((f) => f.page))).sort((a, b) => a - b);
  const shown = pdfFields.filter((f) => pageFilter === 0 || f.page === pageFilter);

  const update = (name: string, type: string, patch: Partial<ContractTemplateField>) => {
    const existing = byName.get(name);
    const next: ContractTemplateField = {
      field_kind: isCheckboxType(type) ? 'checkbox' : 'text', variable: null, pdf_field_name: name, ...existing, ...patch,
    };
    onChange([ ...fields.filter((f) => f.pdf_field_name !== name), next ]);
  };

  return (
    <div>
      <label htmlFor="page_filter" className="text-sm font-medium text-gray-900">Show page </label>
      <select id="page_filter" className="rounded-md py-1 text-sm ring-1 ring-inset ring-gray-300" value={pageFilter} onChange={(e) => setPageFilter(Number(e.target.value))}>
        <option value={0}>All</option>
        {pages.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <table className="mt-3 w-full text-left text-sm sm:max-w-4xl">
        <thead>
          <tr className="text-gray-500"><th className="py-1 pr-2">Page</th><th className="pr-2">Form field</th><th className="pr-2">Fill with</th><th>Tick when value is</th></tr>
        </thead>
        <tbody>
          {shown.map((pf) => {
            const mapped = byName.get(pf.name);
            return (
              <tr key={pf.name} className="border-t border-gray-100">
                <td className="py-1 pr-2">{pf.page}</td>
                <td className="pr-2 break-words">{pf.name} <span className="text-gray-400">({pf.type})</span></td>
                <td className="pr-2">
                  <VariableSelect id={`var_${pf.name}`} value={mapped?.variable ?? ''} variables={variables}
                    onChange={(v) => !readOnly && update(pf.name, pf.type, { variable: v || null })} />
                </td>
                <td>
                  {isCheckboxType(pf.type) && (
                    <input aria-label="Tick when value is" className={inputClass} disabled={readOnly} value={mapped?.on_value ?? ''}
                      onChange={(e) => update(pf.name, pf.type, { on_value: e.target.value })} placeholder="e.g. seller" />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---- Flat PDFs: click on the page where each value should print ------------------------------

const FlatPlacer: FC<{
  templateId: string;
  fields: ContractTemplateField[];
  pages: { number: number; width: number; height: number }[];
  variables: ContractVariable[];
  readOnly: boolean;
  onChange: (fields: ContractTemplateField[]) => void;
}> = ({ templateId, fields, pages, variables, readOnly, onChange }) => {
  const [ page, setPage ] = useState(1);
  const [ selected, setSelected ] = useState<number | null>(null);
  const pageInfo = pages.find((p) => p.number === page);
  const indexed = fields.map((f, index) => ({ f, index }));
  const onThisPage = indexed.filter(({ f }) => f.page === page);
  const label = (key: string | null) => variables.find((v) => v.key === key)?.label ?? 'Pick a variable';

  const handleClick = (clickX: number, clickY: number, scale: number) => {
    if (readOnly || !pageInfo) return;
    const { x, y } = clickToPdfPoint(clickX, clickY, scale, pageInfo.height);
    onChange([ ...fields, { variable: null, field_kind: 'text', page, x, y, font_size: 10 } ]);
    setSelected(fields.length);
  };

  const patch = (index: number, change: Partial<ContractTemplateField>) =>
    onChange(fields.map((f, i) => (i === index ? { ...f, ...change } : f)));
  const current = selected !== null ? fields[selected] : null;

  return (
    <div className="flex flex-wrap gap-6">
      <div>
        <div className="mb-2 flex items-center gap-x-3">
          <button type="button" className={secondaryButton} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-sm text-gray-700">Page {page} of {pages.length}</span>
          <button type="button" className={secondaryButton} disabled={page >= pages.length} onClick={() => setPage(page + 1)}>Next</button>
        </div>
        <PdfPageView url={`/contract_templates/${templateId}/pdf`} pageNumber={page} onPageClick={readOnly ? undefined : handleClick}>
          {(scale) => pageInfo && onThisPage.map(({ f, index }) => {
            const { left, top } = pdfPointToCanvas(f.x ?? 0, f.y ?? 0, scale, pageInfo.height);
            return (
              <button key={index} type="button" onClick={(e) => { e.stopPropagation(); setSelected(index); }}
                style={{ position: 'absolute', left, top: top - 14, fontSize: 11 }}
                className={`rounded px-1 text-white ${selected === index ? 'bg-red-600' : 'bg-indigo-600'}`}>
                {f.field_kind === 'checkbox' ? 'X' : label(f.variable)}
              </button>
            );
          })}
        </PdfPageView>
        {!readOnly && <p className="mt-2 text-xs text-gray-500">Click on the page where a value should print, then choose what goes there.</p>}
      </div>

      <div className="min-w-[16rem] sm:max-w-xs">
        <h3 className="text-sm font-semibold text-gray-900">Placed values ({fields.length})</h3>
        {current && selected !== null && (
          <div className="mt-2 space-y-2 rounded-md border border-gray-200 p-3">
            <VariableSelect id="placement_variable" value={current.variable ?? ''} variables={variables} onChange={(v) => patch(selected, { variable: v || null })} />
            <label className="flex items-center gap-x-2 text-sm"><input type="checkbox" checked={current.field_kind === 'checkbox'}
              onChange={(e) => patch(selected, { field_kind: e.target.checked ? 'checkbox' : 'text' })} />Print an X when ticked (checkbox)</label>
            {current.field_kind === 'checkbox' && (
              <input aria-label="Tick when value is" className={inputClass} placeholder="Tick when value is... (e.g. cash)" value={current.on_value ?? ''}
                onChange={(e) => patch(selected, { on_value: e.target.value })} />
            )}
            <label className="block text-sm">Font size
              <input type="number" className={inputClass} value={current.font_size ?? 10} min={4} max={40}
                onChange={(e) => patch(selected, { font_size: Number(e.target.value) })} /></label>
            {!readOnly && (
              <button type="button" className="text-sm text-red-600" onClick={() => { onChange(fields.filter((_, i) => i !== selected)); setSelected(null); }}>
                Remove this one
              </button>
            )}
          </div>
        )}
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {indexed.map(({ f, index }) => (
            <li key={index}>
              <button type="button" className="w-full py-1 text-left hover:bg-gray-50" onClick={() => { setPage(f.page ?? 1); setSelected(index); }}>
                p{f.page}: {f.field_kind === 'checkbox' ? 'Checkbox - ' : ''}{label(f.variable)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---- The editor ------------------------------------------------------------------------------

const TemplateEditor: FC<{ templateId: string; variables: ContractVariable[]; onClose: () => void }> = ({ templateId, variables, onClose }) => {
  const { template, inspection, isLoading, error } = useContractTemplate(templateId);
  const { saveFields, preview } = useContractTemplateActions();
  const [ fields, setFields ] = useState<ContractTemplateField[]>([]);
  const [ saved, setSaved ] = useState(false);

  useEffect(() => { if (template?.fields) setFields(template.fields); }, [ template ]);

  if (isLoading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (error || !template || !inspection) return <p className="text-sm text-red-600">We could not open this template.</p>;

  const readOnly = !template.editable;
  const change = (next: ContractTemplateField[]) => { setFields(next); setSaved(false); };

  const handleSave = async () => {
    try {
      await saveFields.mutateAsync({ id: templateId, fields: buildFieldsPayload(fields) });
      setSaved(true);
    } catch {
      // shown below from the mutation state
    }
  };

  const handlePreview = async () => {
    try {
      const response = await preview.mutateAsync(templateId);
      window.open(URL.createObjectURL(response.data as Blob), '_blank');
    } catch {
      // shown below from the mutation state
    }
  };

  return (
    <div>
      <button type="button" className="text-sm text-indigo-600" onClick={onClose}>Back to templates</button>
      <h2 className="mt-2 text-base font-semibold text-gray-900">{template.name}</h2>
      <p className="mt-1 text-sm text-gray-700">
        {template.mode === 'fillable'
          ? 'This form has fillable fields. Choose what REIX fills into each one.'
          : 'This form has no fillable fields. Click on each page where a value should print.'}
        {readOnly && ' This is a REIX-provided form, so it is read-only here.'}
      </p>

      <div className="mt-4">
        {template.mode === 'fillable'
          ? <FillableMapper fields={fields} pdfFields={inspection.fields} variables={variables} readOnly={readOnly} onChange={change} />
          : <FlatPlacer templateId={templateId} fields={fields} pages={inspection.pages} variables={variables} readOnly={readOnly} onChange={change} />}
      </div>

      {(saveFields.error || preview.error) && (
        <ul className="mt-3 text-sm text-red-600">{getErrorMessages(saveFields.error || preview.error).map((m) => <li key={m}>{m}</li>)}</ul>
      )}
      <div className="mt-6 flex items-center gap-x-3">
        {!readOnly && <button type="button" className={primaryButton} disabled={saveFields.isPending} onClick={handleSave}>Save</button>}
        <button type="button" className={secondaryButton} disabled={preview.isPending} onClick={handlePreview}>Preview with sample values</button>
        {saved && <span className="text-sm text-green-600">Saved.</span>}
      </div>
    </div>
  );
};

export default TemplateEditor;
